let unsafeRevocationError: AuthRevocationSafetyError | null = null;
let recoveryPersistenceFailed = false;

export const AUTH_REVOCATION_TIMEOUT_MS = 10_000;
export const AUTH_REVOCATION_RECOVERY_MESSAGE =
  'Your previous sign-out could not be reconciled. Please retry signing in or reload this page.';
export const AUTH_REVOCATION_VOLATILE_RECOVERY_MESSAGE =
  'Your previous sign-out needs recovery. Keep this tab open and retry signing in; do not reload or close it.';

const SESSION_RECOVERY_STORAGE_KEY = 'wisefriend.auth.ambiguous-logout-recovery.v1';
const LEGACY_SHARED_REVOCATION_STORAGE_KEY = 'wisefriend.auth.logout-coordination.v1';
const OPERATION_STORAGE_PREFIX = 'wisefriend.auth.logout-operation.v2.';
const LEGACY_OPERATION_ID = 'legacy-v1';
const COORDINATION_CHANNEL_NAME = 'wisefriend.auth.logout-coordination.v1';
const AMBIGUOUS_GATEWAY_STATUSES = new Set([502, 503, 504]);
const SHARED_PENDING_GRACE_MS = 1_000;

export type AuthRevocationCoordinationState = 'clear' | 'pending' | 'recovery';

interface SharedRevocationOperation {
  state: Exclude<AuthRevocationCoordinationState, 'clear'>;
  deadline?: number;
  revision: number;
}

interface OperationMessage {
  kind: 'operation';
  operationId: string;
  operation: SharedRevocationOperation | null;
  revision: number;
}

const sharedOperations = new Map<string, SharedRevocationOperation>();
const operationRevisions = new Map<string, number>();
const persistedOperationIds = new Set<string>();
const stateSubscribers = new Set<(state: AuthRevocationCoordinationState) => void>();
let lastEmittedState: AuthRevocationCoordinationState = 'clear';
let coordinationListenersStarted = false;
let coordinationChannel: BroadcastChannel | null = null;
let operationCounter = 0;
let sessionRecoveryRevision = 0;
let observedOperationEpoch = 0;

export interface AuthRecoverySnapshot {
  recoveryOperations: ReadonlyArray<{
    operationId: string;
    revision: number;
  }>;
  sessionRecoveryRevision: number | null;
  operationEpoch: number;
}

export class AuthRevocationSafetyError extends Error {
  readonly code = 'AUTH_REVOCATION_SAFETY_UNCERTAIN';

  constructor(message = AUTH_REVOCATION_RECOVERY_MESSAGE) {
    super(message);
    this.name = 'AuthRevocationSafetyError';
    Object.setPrototypeOf(this, AuthRevocationSafetyError.prototype);
  }
}

export const isAuthRevocationSafetyError = (
  error: unknown,
): error is AuthRevocationSafetyError => error instanceof AuthRevocationSafetyError;

const operationStorageKey = (operationId: string) => (
  operationId === LEGACY_OPERATION_ID
    ? LEGACY_SHARED_REVOCATION_STORAGE_KEY
    : `${OPERATION_STORAGE_PREFIX}${operationId}`
);

const createOperationId = () => {
  operationCounter += 1;
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${operationCounter.toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const aggregateState = (): AuthRevocationCoordinationState => {
  let hasPending = false;
  for (const operation of sharedOperations.values()) {
    if (operation.state === 'recovery') {
      return 'recovery';
    }
    hasPending = true;
  }
  return hasPending ? 'pending' : 'clear';
};

const emitAggregateState = (notifySubscribers = true) => {
  const state = aggregateState();
  if (state === lastEmittedState) {
    return;
  }
  lastEmittedState = state;
  if (notifySubscribers) {
    stateSubscribers.forEach((subscriber) => subscriber(state));
  }
};

const operationsEqual = (
  left: SharedRevocationOperation,
  right: SharedRevocationOperation,
) => (
  left.state === right.state &&
  left.deadline === right.deadline &&
  left.revision === right.revision
);

const applyOperationEvent = (
  operationId: string,
  operation: SharedRevocationOperation | null,
  revision: number,
  notifySubscribers = true,
) => {
  const currentRevision = operationRevisions.get(operationId) ?? 0;
  const isTombstoned = operationRevisions.has(operationId) && !sharedOperations.has(operationId);
  if (revision < currentRevision || (revision === currentRevision && isTombstoned && operation)) {
    return;
  }

  if (!operation) {
    if (revision === currentRevision && isTombstoned) {
      return;
    }
    if (!operationRevisions.has(operationId)) {
      // Seeing an unknown operation's removal still proves that a logout began
      // after an earlier snapshot, even if its pending event arrived late.
      observedOperationEpoch += 1;
    }
    operationRevisions.set(operationId, revision);
    sharedOperations.delete(operationId);
    emitAggregateState(notifySubscribers);
    return;
  }

  const normalizedOperation = { ...operation, revision };
  const existingOperation = sharedOperations.get(operationId);
  if (existingOperation && operationsEqual(existingOperation, normalizedOperation)) {
    return;
  }
  if (!existingOperation) {
    observedOperationEpoch += 1;
  }
  operationRevisions.set(operationId, revision);
  sharedOperations.set(operationId, normalizedOperation);
  emitAggregateState(notifySubscribers);
};

const parseOperation = (rawValue: string | null): SharedRevocationOperation | null => {
  if (!rawValue) {
    return null;
  }
  try {
    const parsed = JSON.parse(rawValue) as Partial<SharedRevocationOperation>;
    if (parsed.state !== 'pending' && parsed.state !== 'recovery') {
      return null;
    }
    return {
      state: parsed.state,
      deadline:
        parsed.state === 'pending' && typeof parsed.deadline === 'number'
          ? parsed.deadline
          : undefined,
      revision:
        typeof parsed.revision === 'number'
          ? parsed.revision
          : parsed.state === 'recovery' ? 2 : 1,
    };
  } catch {
    return null;
  }
};

const normalizeLegacyRevision = (
  operationId: string,
  operation: SharedRevocationOperation,
  rawValue: string | null,
): SharedRevocationOperation => {
  if (operationId !== LEGACY_OPERATION_ID || !rawValue) {
    return operation;
  }
  try {
    const parsed = JSON.parse(rawValue) as Record<string, unknown>;
    if (typeof parsed.revision === 'number') {
      return operation;
    }
  } catch {
    return operation;
  }

  const currentOperation = sharedOperations.get(operationId);
  if (
    currentOperation &&
    currentOperation.state === operation.state &&
    currentOperation.deadline === operation.deadline
  ) {
    return { ...operation, revision: currentOperation.revision };
  }
  const currentRevision = operationRevisions.get(operationId) ?? 0;
  return operation.revision <= currentRevision
    ? { ...operation, revision: currentRevision + 1 }
    : operation;
};

const applyPersistedOperation = (
  operationId: string,
  rawValue: string | null,
) => {
  const parsedOperation = parseOperation(rawValue);
  const operation = parsedOperation
    ? normalizeLegacyRevision(operationId, parsedOperation, rawValue)
    : null;
  if (operation) {
    persistedOperationIds.add(operationId);
    applyOperationEvent(operationId, operation, operation.revision);
    return;
  }

  persistedOperationIds.delete(operationId);
  applyOperationEvent(
    operationId,
    null,
    (operationRevisions.get(operationId) ?? 0) + 1,
  );
};

const isOperationMessage = (value: unknown): value is OperationMessage => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const message = value as Partial<OperationMessage>;
  return (
    message.kind === 'operation' &&
    typeof message.operationId === 'string' &&
    typeof message.revision === 'number' &&
    (message.operation === null || typeof message.operation === 'object')
  );
};

const ensureCoordinationListeners = () => {
  if (coordinationListenersStarted || typeof window === 'undefined') {
    return;
  }
  coordinationListenersStarted = true;
  window.addEventListener('storage', (event) => {
    if (event.key === LEGACY_SHARED_REVOCATION_STORAGE_KEY) {
      applyPersistedOperation(LEGACY_OPERATION_ID, event.newValue);
      return;
    }
    if (event.key?.startsWith(OPERATION_STORAGE_PREFIX)) {
      applyPersistedOperation(event.key.slice(OPERATION_STORAGE_PREFIX.length), event.newValue);
    }
  });

  if (typeof BroadcastChannel !== 'undefined') {
    coordinationChannel = new BroadcastChannel(COORDINATION_CHANNEL_NAME);
    coordinationChannel.addEventListener('message', (event: MessageEvent<unknown>) => {
      if (isOperationMessage(event.data)) {
        applyOperationEvent(
          event.data.operationId,
          event.data.operation,
          event.data.revision,
        );
        return;
      }

      // Older tabs broadcast the v1 record directly. Treat it as one isolated
      // legacy operation so it cannot clear or overwrite any v2 operation.
      const serializedLegacyOperation = JSON.stringify(event.data);
      const legacyOperation = parseOperation(
        typeof serializedLegacyOperation === 'string' ? serializedLegacyOperation : null,
      );
      if (legacyOperation) {
        const normalizedLegacyOperation = normalizeLegacyRevision(
          LEGACY_OPERATION_ID,
          legacyOperation,
          typeof serializedLegacyOperation === 'string' ? serializedLegacyOperation : null,
        );
        applyOperationEvent(
          LEGACY_OPERATION_ID,
          normalizedLegacyOperation,
          normalizedLegacyOperation.revision,
        );
      } else if (event.data === null) {
        applyOperationEvent(
          LEGACY_OPERATION_ID,
          null,
          (operationRevisions.get(LEGACY_OPERATION_ID) ?? 0) + 1,
        );
      }
    });
  }
};

const broadcastOperation = (
  operationId: string,
  operation: SharedRevocationOperation | null,
  revision: number,
) => {
  ensureCoordinationListeners();
  try {
    coordinationChannel?.postMessage({
      kind: 'operation',
      operationId,
      operation,
      revision,
    } satisfies OperationMessage);
  } catch {
    // Durable per-operation storage still coordinates tabs when the channel fails.
  }
};

const syncPersistedOperations = () => {
  ensureCoordinationListeners();
  const foundOperationIds = new Set<string>();
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key) {
        continue;
      }
      let operationId: string | null = null;
      if (key === LEGACY_SHARED_REVOCATION_STORAGE_KEY) {
        operationId = LEGACY_OPERATION_ID;
      } else if (key.startsWith(OPERATION_STORAGE_PREFIX)) {
        operationId = key.slice(OPERATION_STORAGE_PREFIX.length);
      }
      if (!operationId) {
        continue;
      }
      const rawOperation = localStorage.getItem(key);
      const parsedOperation = parseOperation(rawOperation);
      const operation = parsedOperation
        ? normalizeLegacyRevision(operationId, parsedOperation, rawOperation)
        : null;
      if (!operation) {
        continue;
      }
      foundOperationIds.add(operationId);
      persistedOperationIds.add(operationId);
      applyOperationEvent(operationId, operation, operation.revision);
    }

    Array.from(persistedOperationIds).forEach((operationId) => {
      if (!foundOperationIds.has(operationId)) {
        persistedOperationIds.delete(operationId);
        applyOperationEvent(
          operationId,
          null,
          (operationRevisions.get(operationId) ?? 0) + 1,
        );
      }
    });
  } catch {
    // Keep current-lifecycle and BroadcastChannel state when storage is unavailable.
  }
};

const writeOperation = (
  operationId: string,
  operation: SharedRevocationOperation,
): boolean => {
  // The initiating tab signs itself out only after the authenticated request has
  // been constructed. Suppress its own pending notification; storage events and
  // BroadcastChannel still notify every other tab before the request starts.
  applyOperationEvent(
    operationId,
    operation,
    operation.revision,
    operation.state !== 'pending',
  );
  let persisted = false;
  try {
    localStorage.setItem(operationStorageKey(operationId), JSON.stringify(operation));
    persistedOperationIds.add(operationId);
    persisted = true;
  } catch {
    // BroadcastChannel and in-memory state protect currently open tabs.
  }
  broadcastOperation(operationId, operation, operation.revision);
  return persisted;
};

const removeOperation = (operationId: string): boolean => {
  const currentRevision = operationRevisions.get(operationId) ?? 0;
  const removalRevision = currentRevision + 1;
  const wasPersisted = persistedOperationIds.has(operationId);
  let storageRemoved = false;
  try {
    const storageKey = operationStorageKey(operationId);
    localStorage.removeItem(storageKey);
    storageRemoved = localStorage.getItem(storageKey) === null;
  } catch {
    // A previously durable marker must remain active if its removal cannot be verified.
  }

  if (!storageRemoved && wasPersisted) {
    return false;
  }

  persistedOperationIds.delete(operationId);
  applyOperationEvent(operationId, null, removalRevision);
  broadcastOperation(operationId, null, removalRevision);
  return true;
};

const markOperationPending = (operationId: string, timeoutMs: number) => {
  writeOperation(operationId, {
    state: 'pending',
    deadline: Date.now() + timeoutMs + SHARED_PENDING_GRACE_MS,
    revision: 1,
  });
};

const persistSessionRecoveryMarker = () => {
  try {
    const alreadyPersisted = sessionStorage.getItem(SESSION_RECOVERY_STORAGE_KEY) === '1';
    sessionStorage.setItem(SESSION_RECOVERY_STORAGE_KEY, '1');
    if (!alreadyPersisted || sessionRecoveryRevision === 0) {
      sessionRecoveryRevision += 1;
    }
    return true;
  } catch {
    return false;
  }
};

const promoteOperationToRecovery = (operationId: string): boolean => {
  const currentOperation = sharedOperations.get(operationId);
  if (!currentOperation || currentOperation.state === 'recovery') {
    return currentOperation?.state === 'recovery';
  }
  const recoveryOperation: SharedRevocationOperation = {
    state: 'recovery',
    revision: currentOperation.revision + 1,
  };
  const sessionPersisted = persistSessionRecoveryMarker();
  const sharedPersisted = writeOperation(operationId, recoveryOperation);
  if (!sessionPersisted && !sharedPersisted) {
    recoveryPersistenceFailed = true;
    unsafeRevocationError = new AuthRevocationSafetyError(
      AUTH_REVOCATION_VOLATILE_RECOVERY_MESSAGE,
    );
  }
  return true;
};

const promoteExpiredPendingOperations = () => {
  const now = Date.now();
  Array.from(sharedOperations.entries()).forEach(([operationId, operation]) => {
    if (
      operation.state === 'pending' &&
      typeof operation.deadline === 'number' &&
      operation.deadline <= now
    ) {
      promoteOperationToRecovery(operationId);
    }
  });
};

const refreshSharedOperations = () => {
  syncPersistedOperations();
  promoteExpiredPendingOperations();
};

const hasRecoveryOperation = () => (
  Array.from(sharedOperations.values()).some((operation) => operation.state === 'recovery')
);

const getPendingDeadline = (): number | null => {
  let nearestDeadline: number | null = null;
  sharedOperations.forEach((operation) => {
    if (operation.state !== 'pending') {
      return;
    }
    const deadline = operation.deadline ?? Date.now();
    nearestDeadline = nearestDeadline === null ? deadline : Math.min(nearestDeadline, deadline);
  });
  return nearestDeadline;
};

const readSessionRecoveryRevision = (): number | null => {
  try {
    if (sessionStorage.getItem(SESSION_RECOVERY_STORAGE_KEY) !== '1') {
      return null;
    }
    if (sessionRecoveryRevision === 0) {
      sessionRecoveryRevision = 1;
    }
    return sessionRecoveryRevision;
  } catch {
    return null;
  }
};

export const captureAuthRecoverySnapshot = (): AuthRecoverySnapshot => {
  refreshSharedOperations();
  return {
    recoveryOperations: Array.from(sharedOperations.entries())
      .filter(([, operation]) => operation.state === 'recovery')
      .map(([operationId, operation]) => ({
        operationId,
        revision: operation.revision,
      }))
      .sort((left, right) => left.operationId.localeCompare(right.operationId)),
    sessionRecoveryRevision: readSessionRecoveryRevision(),
    operationEpoch: observedOperationEpoch,
  };
};

export const authRecoverySnapshotRequiresHandshake = (
  snapshot: AuthRecoverySnapshot,
) => (
  snapshot.recoveryOperations.length > 0 || snapshot.sessionRecoveryRevision !== null
);

export const assertAuthRecoverySnapshotCurrent = (
  snapshot: AuthRecoverySnapshot,
): void => {
  refreshSharedOperations();
  if (snapshot.operationEpoch !== observedOperationEpoch) {
    throw getAuthRevocationSafetyError();
  }
  const coveredOperations = new Map(
    snapshot.recoveryOperations.map(({ operationId, revision }) => [operationId, revision]),
  );
  for (const { operationId, revision } of snapshot.recoveryOperations) {
    const currentOperation = sharedOperations.get(operationId);
    if (
      currentOperation?.state !== 'recovery' ||
      currentOperation.revision !== revision
    ) {
      throw getAuthRevocationSafetyError();
    }
  }
  for (const [operationId, operation] of sharedOperations.entries()) {
    if (
      operation.state === 'pending' ||
      coveredOperations.get(operationId) !== operation.revision
    ) {
      throw getAuthRevocationSafetyError();
    }
  }

  const currentSessionRevision = readSessionRecoveryRevision();
  if (snapshot.sessionRecoveryRevision !== currentSessionRevision) {
    throw getAuthRevocationSafetyError();
  }
};

export const requiresAmbiguousLogoutRecovery = (): boolean => {
  refreshSharedOperations();
  if (hasRecoveryOperation()) {
    return true;
  }
  return readSessionRecoveryRevision() !== null;
};

export const getAuthRevocationSafetyError = (): AuthRevocationSafetyError => (
  unsafeRevocationError ?? new AuthRevocationSafetyError(
    recoveryPersistenceFailed
      ? AUTH_REVOCATION_VOLATILE_RECOVERY_MESSAGE
      : AUTH_REVOCATION_RECOVERY_MESSAGE,
  )
);

export const completeAmbiguousLogoutRecovery = (
  snapshot: AuthRecoverySnapshot,
): void => {
  assertAuthRecoverySnapshotCurrent(snapshot);
  for (const { operationId, revision } of snapshot.recoveryOperations) {
    const currentOperation = sharedOperations.get(operationId);
    if (
      currentOperation?.state === 'recovery' &&
      currentOperation.revision === revision &&
      !removeOperation(operationId)
    ) {
      throw getAuthRevocationSafetyError();
    }
  }

  if (snapshot.sessionRecoveryRevision !== null && !hasRecoveryOperation()) {
    try {
      sessionStorage.removeItem(SESSION_RECOVERY_STORAGE_KEY);
      if (sessionStorage.getItem(SESSION_RECOVERY_STORAGE_KEY) === '1') {
        throw getAuthRevocationSafetyError();
      }
    } catch (error) {
      if (isAuthRevocationSafetyError(error)) {
        throw error;
      }
      throw getAuthRevocationSafetyError();
    }
  }

  refreshSharedOperations();
  if (aggregateState() !== 'clear' || readSessionRecoveryRevision() !== null) {
    throw getAuthRevocationSafetyError();
  }
  recoveryPersistenceFailed = false;
  unsafeRevocationError = null;
  emitAggregateState();
};

// Test/reset compatibility helper. Product completion must always use the
// snapshot-bound function above so it cannot clear a newer logout operation.
export const clearAmbiguousLogoutRecovery = (): void => {
  refreshSharedOperations();
  Array.from(sharedOperations.entries()).forEach(([operationId, operation]) => {
    if (operation.state === 'recovery') {
      removeOperation(operationId);
    }
  });
  try {
    sessionStorage.removeItem(SESSION_RECOVERY_STORAGE_KEY);
  } catch {
    // Focused tests can still clear volatile per-operation state below.
  }
  if (!hasRecoveryOperation()) {
    recoveryPersistenceFailed = false;
    unsafeRevocationError = null;
  }
  emitAggregateState();
};

export const getAuthRevocationCoordinationState = (): AuthRevocationCoordinationState => {
  refreshSharedOperations();
  return aggregateState();
};

export const subscribeToAuthRevocationState = (
  subscriber: (state: AuthRevocationCoordinationState) => void,
): (() => void) => {
  ensureCoordinationListeners();
  stateSubscribers.add(subscriber);
  return () => stateSubscribers.delete(subscriber);
};

const waitForSharedStateChange = (timeoutMs: number) => new Promise<void>((resolve) => {
  const timeoutId = setTimeout(() => {
    unsubscribe();
    resolve();
  }, Math.max(0, timeoutMs));
  const unsubscribe = subscribeToAuthRevocationState(() => {
    clearTimeout(timeoutId);
    unsubscribe();
    resolve();
  });
});

const getServerResponseStatus = (error: unknown): number | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const status = (error as { response?: { status?: unknown } }).response?.status;
  return typeof status === 'number' ? status : null;
};

/** Bound and cancel outgoing server logout work. */
export const createBoundedAuthRevocation = <T>(
  requestFactory: (signal: AbortSignal) => Promise<T>,
  timeoutMs = AUTH_REVOCATION_TIMEOUT_MS,
): Promise<T> => {
  const controller = new AbortController();

  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      callback();
    };
    const timeoutId = setTimeout(() => {
      controller.abort();
      finish(() => reject(new AuthRevocationSafetyError()));
    }, timeoutMs);

    let request: Promise<T>;
    try {
      request = requestFactory(controller.signal);
    } catch (error) {
      finish(() => reject(error));
      return;
    }

    void request.then(
      (value) => finish(() => resolve(value)),
      (error) => {
        const responseStatus = getServerResponseStatus(error);
        if (responseStatus !== null && !AMBIGUOUS_GATEWAY_STATUSES.has(responseStatus)) {
          finish(() => reject(error));
          return;
        }
        controller.abort();
        finish(() => reject(new AuthRevocationSafetyError()));
      },
    );
  });
};

export const registerPendingAuthRevocation = <T>(
  request: Promise<T>,
  operationId = createOperationId(),
): Promise<T> => {
  if (!sharedOperations.has(operationId)) {
    markOperationPending(operationId, AUTH_REVOCATION_TIMEOUT_MS);
  }

  void request.then(
    () => {
      removeOperation(operationId);
    },
    (error) => {
      if (isAuthRevocationSafetyError(error)) {
        promoteOperationToRecovery(operationId);
      } else {
        removeOperation(operationId);
      }
    },
  );

  return request;
};

export const waitForPendingAuthRevocations = async (): Promise<void> => {
  while (true) {
    refreshSharedOperations();
    const pendingDeadline = getPendingDeadline();
    if (pendingDeadline === null) {
      break;
    }
    const remaining = Math.max(0, pendingDeadline - Date.now());
    await waitForSharedStateChange(Math.min(remaining, 1_000));
  }

  // Volatile recovery is permitted in this lifecycle. Its safety error remains
  // available and is surfaced if the backend handshake cannot be confirmed.
  if (unsafeRevocationError && !hasRecoveryOperation()) {
    throw unsafeRevocationError;
  }
};

export const startBoundedAuthRevocation = <T>(
  requestFactory: (signal: AbortSignal) => Promise<T>,
  timeoutMs = AUTH_REVOCATION_TIMEOUT_MS,
): Promise<T> => {
  const operationId = createOperationId();
  markOperationPending(operationId, timeoutMs);
  return registerPendingAuthRevocation(
    createBoundedAuthRevocation(requestFactory, timeoutMs),
    operationId,
  );
};
